import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AUTOSAVE_DEBOUNCE_MS,
  AUTOSAVE_FLUSH_INTERVAL_MS,
  AUTOSAVE_MAX_BACKOFF_MS,
  type SaveAnswersResult,
  type SubmittedAnswer,
} from 'shared';
import { ApiError, apiFetch } from '@/lib/api';
import { answersSignature, writeDraft, type AnswerDraft } from '@/lib/exam-session';

export type SaveStatus = 'saved' | 'saving' | 'pending' | 'offline' | 'expired';

export type UseAutoSaveOptions = {
  attemptId: string;
  sessionToken: string;
  answers: Record<string, AnswerDraft>;
  /** คำตอบที่ server ยืนยันแล้วตอนเข้าหน้า — ใช้เป็นจุดตั้งต้นว่าอะไรยังไม่ถูกบันทึก */
  syncedAnswers: Record<string, AnswerDraft>;
  enabled: boolean;
  onSaved?: (result: SaveAnswersResult) => void;
  onExpired?: () => void;
};

export type AutoSave = {
  status: SaveStatus;
  /** บันทึกทันที (เปลี่ยนหน้า / กดส่ง / สลับแท็บ) */
  flush: () => Promise<void>;
  hasUnsavedChanges: boolean;
};

function toSubmittedAnswers(answers: Record<string, AnswerDraft>): SubmittedAnswer[] {
  return Object.entries(answers).map(([questionId, draft]) => ({
    questionId,
    selectedOptionIds: draft.selectedOptionIds,
    textAnswer: draft.textAnswer,
    isFlagged: draft.isFlagged,
  }));
}

/**
 * Auto-save ที่ทนต่อเน็ตหลุด
 * 1. เก็บลง localStorage ทุกครั้งที่เปลี่ยน  2. debounce 2 วิ  3. flush ทุก 20 วิ
 * 4. ล้มเหลว → retry แบบ exponential backoff (สูงสุด 30 วิ)
 * ส่งคำตอบทั้งชุดทุกครั้ง (idempotent) → draft ในเครื่องที่ใหม่กว่าถูกดันขึ้น server เสมอ
 */
export function useAutoSave({
  attemptId,
  sessionToken,
  answers,
  syncedAnswers,
  enabled,
  onSaved,
  onExpired,
}: UseAutoSaveOptions): AutoSave {
  const [status, setStatus] = useState<SaveStatus>('saved');
  const answersRef = useRef(answers);
  const dirtyRef = useRef(false);
  const savingRef = useRef(false);
  const backoffRef = useRef(1_000);
  const retryTimer = useRef<number | null>(null);
  const debounceTimer = useRef<number | null>(null);
  const callbacks = useRef({ onSaved, onExpired });
  /** ลายเซ็นของคำตอบชุดล่าสุดที่ server รับไปแล้ว */
  const syncedRef = useRef<string | null>(null);

  callbacks.current = { onSaved, onExpired };
  answersRef.current = answers;

  // ตั้งจุดตั้งต้นครั้งเดียวตอนเริ่มทำงาน — ของที่ server มีอยู่แล้วไม่ต้องส่งซ้ำ
  if (syncedRef.current === null && enabled) {
    syncedRef.current = answersSignature(syncedAnswers);
  }

  const flush = useCallback(async (): Promise<void> => {
    if (!enabled || !dirtyRef.current || savingRef.current) return;

    savingRef.current = true;
    setStatus('saving');
    const snapshot = answersRef.current;

    try {
      const result = await apiFetch<SaveAnswersResult>('/public/attempt/answers', {
        method: 'PATCH',
        sessionToken,
        body: { answers: toSubmittedAnswers(snapshot) },
      });

      // ระหว่างที่กำลังบันทึก ผู้เรียนอาจเปลี่ยนคำตอบเพิ่ม — ยังถือว่ามีของค้าง
      syncedRef.current = answersSignature(snapshot);
      dirtyRef.current = answersSignature(answersRef.current) !== syncedRef.current;
      backoffRef.current = 1_000;
      setStatus(dirtyRef.current ? 'pending' : 'saved');
      callbacks.current.onSaved?.(result);
    } catch (error) {
      if (error instanceof ApiError && error.code === 'ATTEMPT_EXPIRED') {
        dirtyRef.current = false;
        setStatus('expired');
        callbacks.current.onExpired?.();
        return;
      }

      // เน็ตหลุดหรือ server ไม่ตอบ — คำตอบยังอยู่ใน localStorage แล้วค่อยลองใหม่
      setStatus('offline');
      const delay = Math.min(backoffRef.current, AUTOSAVE_MAX_BACKOFF_MS);
      backoffRef.current = Math.min(backoffRef.current * 2, AUTOSAVE_MAX_BACKOFF_MS);
      if (retryTimer.current) window.clearTimeout(retryTimer.current);
      retryTimer.current = window.setTimeout(() => void flush(), delay);
    } finally {
      savingRef.current = false;
    }
  }, [enabled, sessionToken]);

  /**
   * เปลี่ยนคำตอบ → เก็บลงเครื่องทันที แล้ว debounce ก่อนยิงขึ้น server
   * ยิงเฉพาะเมื่อต่างจากที่ server มีจริง — ตอนเพิ่งเปิดหน้าข้อสอบยังไม่มีอะไรเปลี่ยน
   * จึงไม่ควรยิง (ถ้ายิงแล้วพลาด ผู้เรียนจะเห็นคำเตือน "ออฟไลน์" ทั้งที่ยังไม่ได้ทำอะไร)
   * แต่ถ้ามี draft ค้างในเครื่องจากตอนเน็ตหลุด ลายเซ็นจะต่างจาก server และถูกส่งขึ้นทันที
   */
  useEffect(() => {
    if (!enabled) return;
    writeDraft({ attemptId, updatedAt: Date.now(), answers });

    dirtyRef.current = answersSignature(answers) !== syncedRef.current;
    if (!dirtyRef.current) {
      setStatus((current) => (current === 'expired' ? current : 'saved'));
      return;
    }

    setStatus((current) => (current === 'expired' ? current : 'pending'));

    if (debounceTimer.current) window.clearTimeout(debounceTimer.current);
    debounceTimer.current = window.setTimeout(() => void flush(), AUTOSAVE_DEBOUNCE_MS);

    return () => {
      if (debounceTimer.current) window.clearTimeout(debounceTimer.current);
    };
  }, [answers, attemptId, enabled, flush]);

  // flush เป็นระยะเผื่อ debounce ถูกเลื่อนตลอดเพราะพิมพ์ต่อเนื่อง
  useEffect(() => {
    if (!enabled) return;
    const interval = window.setInterval(() => void flush(), AUTOSAVE_FLUSH_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [enabled, flush]);

  // สลับแท็บ / ปิดหน้าจอมือถือ → บันทึกทันที
  useEffect(() => {
    if (!enabled) return;
    const onVisibility = (): void => {
      if (document.visibilityState === 'hidden') void flush();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [enabled, flush]);

  useEffect(() => {
    return () => {
      if (retryTimer.current) window.clearTimeout(retryTimer.current);
    };
  }, []);

  return { status, flush, hasUnsavedChanges: dirtyRef.current };
}
